import { Button } from '@mui/material';
import css from './Button.module.scss';
import { memo, PropsWithChildren } from 'react';
import classNames from 'classnames';

export interface ComponentProps extends PropsWithChildren {
    /**
     * Callback for click event
     */
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /**
     * Whether button is disabled. Will apply disabled style
     */
    disabled?: boolean;
    /**
     * Controls the style of the button based on the button type
     *
     * primary_button -- BG: KeyTrak Blue; Text: White
     * secondary_button - BG: White; Text: KeyTrak Blue
     * alt_primary_button_dark_page -- BG: white; Text: KeyTrak Blue
     * alt_secondary_button_dark_page -- BG: KeyTrak Blue; Text: White
     * alt_primary_button_light_page -- BG: KeyTrak Blue; Text: White
     * alt_secondary_button_light_page -- BG: White; Text: KeyTrak Blue
     * text - background is transparent, text is primary button color, no border
     * custom - does not apply any background or color. A className should be provided to style appropriately
     */
    buttonType: 'primary_button' | 'secondary_button' |
    'alt_primary_button_dark_page' | 'alt_secondary_button_dark_page' |
    'alt_primary_button_light_page' | 'alt_secondary_button_light_page' |
    'text' | 'custom';
    /**
     * Optional styles to apply
     */
    className?: string;
    /**
     * Whether this is a submit button
     */
    submit?: boolean;
    /**
     * Dictates the font size, height, and min-width of the button. Default 'normal'
     *
     * Larger sizes will shrink at smaller breakpoints
     */
    size?: 'normal' | 'large' | 'small' | 'xl';
    /**
     * Icon to display on the button. Position is determined by `iconPosition`
     */
    icon?: JSX.Element;
    /**
     * Positioning of icon and label text. Default 'right'
     *
     * right - label text is left aligned, icon is right aligned
     * left - label text is right aligned, icon is left aligned
     * beside - icon is to the right of the label text, but both are centered
     */
    iconPosition?: 'left' | 'right' | 'beside';
    /**
     * Whether the button should take up the full width of parent
     */
    fullWidth?: boolean;
    /**
     * Whether the button icon should have sizing applied
     */
    unsizedIcon?: boolean;
    /**
     * Controls outer margin. Default 'normal'
     *
     * normal - margin all around
     * sides - only left and right margin
     * topbottom - only top and bottom margin
     * none - no margin
     */
    margin?: 'normal' | 'sides' | 'topbottom' | 'none';
    /**
     * Makes the button have rounded edges and reduced left and right padding
     */
    rounded?: boolean;
}

export const GMButton: React.FC<ComponentProps> = memo(({ size = 'normal', iconPosition = 'right', margin = 'normal', ...props }) => {

    const getButtonVariant = () => {
        switch (props.buttonType) {
            case 'primary_button':
            case 'alt_primary_button_dark_page':
            case 'alt_primary_button_light_page':
                return 'contained';
            case 'secondary_button':
            case 'alt_secondary_button_dark_page':
            case 'alt_secondary_button_light_page':
            case 'custom':
                return 'outlined';
            default:
                return 'text';
        }
    };

    return (
        <Button
            className={classNames(css.root, {
                [css.normal]: size === 'normal',
                [css.large]: size === 'large',
                [css.small]: size === 'small',
                [css.xl]: size === 'xl',
                [css.primary_button]: props.buttonType === 'primary_button',
                [css.secondary_button]: props.buttonType === 'secondary_button',
                [css.alt_primary_button_dark_page]: props.buttonType === 'alt_primary_button_dark_page',
                [css.alt_secondary_button_dark_page]: props.buttonType === 'alt_secondary_button_dark_page',
                [css.alt_primary_button_light_page]: props.buttonType === 'alt_primary_button_light_page',
                [css.alt_secondary_button_light_page]: props.buttonType === 'alt_secondary_button_light_page',
                [css.text]: props.buttonType === 'text',
                [css.fullWidth]: props.fullWidth,
                [css.sizelessIcon]: props.unsizedIcon,
                [css.normalMargin]: margin === 'normal',
                [css.sidesMargin]: margin === 'sides',
                [css.topBottomMargin]: margin === 'topbottom',
                [css.noMargin]: margin === 'none',
                [css.rounded]: props.rounded,
            },
            props.className,
            )}
            variant={getButtonVariant()}
            onClick={(!props.disabled && props.onClick) || undefined}
            disabled={props.disabled}
            type={props.submit ? 'submit' : undefined}
        >
            <div className={css.content}>
                {iconPosition === 'right' || iconPosition === 'beside' ? props.children : null}
                {props.icon &&
                    <div
                        className={classNames(css.icon, {
                            [css.right]: iconPosition === 'right',
                            [css.left]: iconPosition === 'left',
                            [css.beside]: iconPosition === 'beside',
                        })}
                    >
                        {props.icon}
                    </div>
                }
                {iconPosition === 'left' ? props.children : null}
            </div>
        </Button>
    );
});
